import React, { useEffect, useState } from "react";

import { useNavigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import useMediaQuery from '@mui/material/useMediaQuery';
import { styled } from '@mui/material/styles';

import {Box, Stack, Drawer, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography, Avatar} from '@mui/material';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { Type, isDrawerOpen, drawerMaxWidth, drawerMinWidth, SuperAdminDefinition, AdminDefinition, CommanderDefinition, UserDefinition, ManagerDefinition } from './def';

import { useMenuReplay } from '../../context/MenuReplayContext';
import { AppIcon } from '../appIcon';
import logoS from '../../assets/images/logo_S.png';
import heartKiumLogo from '../../assets/images/heart-kium-logo.png';
import noPhotoIcon from '../../assets/images/noPhoto.png';
import icon20 from '../../assets/images/header/icon20.png';
  
const DrawerMenus = styled(Drawer, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme }) => ({
  width: drawerMaxWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  '& .MuiDrawer-paper': {
    backgroundColor: '#FDFBFE',
    width: drawerMaxWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
    borderRight: '1px solid #E0E0E0'
  },
  variants: [
    {
      props: ({ open }) => open,
      style: {
        ...openedDrawerMenus(theme),
        '& .MuiDrawer-paper': openedDrawerMenus(theme),
      },
    },
    {
      props: ({ open }) => !open,
      style: {
        ...closedDrawerMenus(theme),
        '& .MuiDrawer-paper': closedDrawerMenus(theme),
      },
    },
  ],
}));

const DrawerMenusHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  paddingLeft: theme.spacing(2),
  height: 64,
  minHeight: 64,
  borderBottom: '1px solid #E0E0E0',
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const DrawerMenusContent = styled('div')(({ theme, open }) => ({
  flexGrow: 1,
  overflow: open ? 'auto' : 'hidden',
  minHeight: 500,
  '&::-webkit-scrollbar': {
    display: 'none'
  },
  msOverflowStyle: 'none',
  scrollbarWidth: 'none'
}));

const DrawerMenusFooter = styled('div')(({ theme }) => ({
  marginTop: 'auto',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  paddingLeft: theme.spacing(2),
  height: 64
}));

const openedDrawerMenus = (theme) => ({
  width: drawerMaxWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedDrawerMenus = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

export const Menus = ({appType, setMenusWidth, setPageWidth}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { bumpMenuReplay } = useMenuReplay();

  const isMobile = useMediaQuery("(max-width: 768px)");
  const isTablet = useMediaQuery("(min-width: 768px) and (max-width: 1024px)");

  const [breadcrumb, setBreadcrumb] = useState(['']);

  const [menuOpen, setMenuOpen] = useState(isDrawerOpen);

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const [menus, setMenus] = useState([]);

  const [hospitalName, setHospitalName] = useState('');
  const [meInfo, setMeInfo] = useState(null);

  useEffect(() => {
    function resize() { setWindowWidth(window.innerWidth); };
    window.addEventListener("resize", resize);  
    return () => { window.removeEventListener("resize", resize);};
  }, []);

  // meInfo 상태 업데이트를 위한 useEffect
  useEffect(() => {
    const storedMeInfo = JSON.parse(localStorage.getItem('meInfo'));
    setMeInfo(storedMeInfo);
  }, []);

  // localStorage 변경 감지를 위한 이벤트 리스너
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'meInfo') {
        const newMeInfo = e.newValue ? JSON.parse(e.newValue) : null;
        setMeInfo(newMeInfo);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // 커스텀 이벤트 리스너 (같은 탭에서의 localStorage 변경 감지)
    const handleMeInfoChange = () => {
      const storedMeInfo = JSON.parse(localStorage.getItem('meInfo'));
      setMeInfo(storedMeInfo);
    };

    window.addEventListener('meInfoChanged', handleMeInfoChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('meInfoChanged', handleMeInfoChange);
    };
  }, []);

  useEffect(() => {
    // meInfo가 없어도 appType에 따라 기본 메뉴 설정
    setHospitalName(meInfo?.hospitalName || '');

    let menuDefinition = [];

    if (appType === 'commander') {
      menuDefinition = CommanderDefinition;
    } else if (meInfo?.grade === 100) {// && sessionStorage.getItem('selectedHospitalId') === null) {
      menuDefinition = SuperAdminDefinition;
    } else if (appType === 'admin') {// || sessionStorage.getItem('selectedHospitalId') !== null) {
      menuDefinition = AdminDefinition;
    } else {      
      menuDefinition = meInfo?.grade >= 50 ? ManagerDefinition : UserDefinition;      
    }    

    // Transform menu items to include translated names
    const translatedMenus = menuDefinition.map(menu => ({
      ...menu,
      displayName: t(menu.name) // Add translated display name
    }));
    setMenus(translatedMenus);
  }, [t, appType, meInfo]); // meInfo 상태 변경 감지

  useEffect(() => {
    // menus가 로드되지 않았으면 breadcrumb를 설정하지 않음
    if (menus.length === 0) {
      return;
    }

    if (location.pathname === '/') {
      setBreadcrumb([menus[0]?.displayName]);
    } else {
      // 현재 경로에 해당하는 메뉴 찾기
      const currentMenu = menus.find(menu => menu.path === location.pathname);
      if (currentMenu) {
        setBreadcrumb([currentMenu.displayName]);
      } else {
        // 하위메뉴인 경우 상위메뉴와 하위메뉴 찾기
        const parentMenu = menus.find(menu => {
          // 현재 경로가 메뉴의 path로 시작하는지 확인
          return location.pathname.startsWith(menu.path) && menu.path !== '/';
        });
        
        if (parentMenu) {
          // 상위메뉴를 찾은 경우
          let breadcrumbItems = [parentMenu.displayName];
          
          // 하위메뉴가 있는 경우
          if (parentMenu.subMenus) {
            const subMenu = parentMenu.subMenus.find(sub => sub.path === location.pathname);
            if (subMenu) {
              breadcrumbItems.push(t(subMenu.name));
            } else {
              // 정확한 하위메뉴를 찾을 수 없는 경우 경로에서 추출
              const pathParts = location.pathname.replace(parentMenu.path, '').split('/').filter(Boolean);
              if (pathParts.length > 0) {
                const lastPart = pathParts[pathParts.length - 1];
                // 숫자나 ID인 경우 건너뛰기
                if (!/^\d+$/.test(lastPart)) {
                  breadcrumbItems.push(lastPart.charAt(0).toUpperCase() + lastPart.slice(1));
                }
              }
            }
          }
          
          setBreadcrumb(breadcrumbItems);
        } else {
          // 메뉴에서 찾을 수 없는 경우 기존 방식 사용
          setBreadcrumb(location.pathname.split("/").filter(Boolean).map(str => str.charAt(0).toUpperCase() + str.slice(1)));
        }
      }
    }
  }, [location.pathname, menus, t]);

  useEffect(() => {
    if(isMobile || isTablet || appType === 'commander') {
      setMenusWidth(0);
      setPageWidth(windowWidth - drawerMaxWidth);
    }
    else{
      setMenusWidth(menuOpen ? drawerMaxWidth : drawerMinWidth);
      setPageWidth(windowWidth - (menuOpen ? drawerMaxWidth : drawerMinWidth));
    }
  }, [appType, menuOpen, isMobile, isTablet, windowWidth, setMenusWidth, setPageWidth]);

  const runNavigation = (path) => {
    const target = path === '/dashboard' ? '/' : path;

    // 이미 해당 URL이면: 앱 홈이 아니라 이 화면을 초기 상태로(라우트 리마운트)
    if (target === location.pathname) {
      bumpMenuReplay();
      if (menus.length === 0) {
        return;
      }
      if (target === '/') {
        setBreadcrumb([menus[0]?.displayName]);
      } else {
        const currentMenu = menus.find((menu) => menu.path === target);
        if (currentMenu) {
          setBreadcrumb([currentMenu.displayName]);
        } else {
          const parentMenu = menus.find((menu) => target.startsWith(menu.path) && menu.path !== '/');
          if (parentMenu) {
            const breadcrumbItems = [parentMenu.displayName];
            if (parentMenu.subMenus) {
              const subMenu = parentMenu.subMenus.find((sub) => sub.path === target);
              if (subMenu) {
                breadcrumbItems.push(t(subMenu.name));
              }
            }
            setBreadcrumb(breadcrumbItems);
          } else {
            setBreadcrumb(
              target.split('/').filter(Boolean).map((str) => str.charAt(0).toUpperCase() + str.slice(1))
            );
          }
        }
      }
      return;
    }

    navigate(target);

    // menus가 로드되지 않았으면 breadcrumb를 설정하지 않음
    if (menus.length === 0) {
      return;
    }

    if (target === '/') {
      setBreadcrumb([menus[0]?.displayName]);
    } else {
      // 현재 경로에 해당하는 메뉴 찾기
      const currentMenu = menus.find((menu) => menu.path === target);
      if (currentMenu) {
        setBreadcrumb([currentMenu.displayName]);
      } else {
        // 하위메뉴인 경우 상위메뉴와 하위메뉴 찾기
        const parentMenu = menus.find((menu) => target.startsWith(menu.path) && menu.path !== '/');

        if (parentMenu) {
          const breadcrumbItems = [parentMenu.displayName];

          if (parentMenu.subMenus) {
            const subMenu = parentMenu.subMenus.find((sub) => sub.path === target);
            if (subMenu) {
              breadcrumbItems.push(t(subMenu.name));
            }
          }

          setBreadcrumb(breadcrumbItems);
        } else {
          // 메뉴에서 찾을 수 없는 경우에만 path 사용
          setBreadcrumb(
            target.split('/').filter(Boolean).map((str) => str.charAt(0).toUpperCase() + str.slice(1))
          );
        }
      }
    }
  };

  return (
    <>
      {isMobile || isTablet || appType === 'commander' ?
      <>
      <Stack direction="row" alignItems="center" gap={1}>
        
        {(menus.length > 1 || isMobile) &&
          <img src={icon20} alt="icon20" style={{ height: '20px' }} onClick={() => setMenuOpen(true)}/>
        }

         <Stack direction="row" alignItems="center" gap={1}>
          {((isMobile && JSON.parse(localStorage.getItem('meInfo'))?.grade === 1) || appType === 'commander' )
          ? <img src={heartKiumLogo} alt="HK Logo" style={{ height: isMobile ? '20px' : '30px', width: isMobile ? 'auto' : '145px' }} onClick={() => runNavigation('/')}/>
          : !isMobile && <img src={logoS} alt="HK Logo" style={{ height: '32px' }} onClick={() => runNavigation('/')}/>}
         
          {(!isMobile || JSON.parse(localStorage.getItem('meInfo'))?.grade === 50) &&            
            <Typography 
              variant="subtitle1" 
              component="div"
              sx={{
                color: '#333333',
                fontSize: '16px',
                fontWeight: 500
              }}
              onClick={() => runNavigation('/')}
            >
              {hospitalName || t('app_name')} {appType === 'commander' && (() => {
                try {
                  const commanderInfo = localStorage.getItem('commanderInfo');
                  if (!commanderInfo || commanderInfo.length === 0) return '';
                  const parsedInfo = JSON.parse(commanderInfo);
                  if (!parsedInfo || typeof parsedInfo !== 'string' || parsedInfo.trim() === '') return '';
                  return `(${parsedInfo})`;
                } catch (error) {
                  return '';
                }
              })()}
            </Typography>            
          }          
        </Stack>
      </Stack>
      
      <Drawer 
        open={menuOpen} 
        sx={{
          width: drawerMaxWidth,
          '& .MuiDrawer-paper': {
            backgroundColor: '#FDFBFE',
            width: drawerMaxWidth
          }
        }} 
        onClose={()=>{setMenuOpen(false)}}
      >
        <Box role="presentation" sx={{ marginTop: 2, width: drawerMaxWidth}} onClick={()=>{setMenuOpen(false)}}>
          <Stack direction="row" alignItems="center" gap={1} sx={{ px: 2, mb: 2 }}>
            <img src={logoS} alt="HK Logo" style={{ height: '32px' }} onClick={() => runNavigation('/')}/>
            {!isMobile && <Typography 
              variant="subtitle1" 
              component="div"
              sx={{
                color: '#333333',
                fontSize: '16px',
                fontWeight: 500
              }}
              onClick={() => runNavigation('/')}
            > 
              {hospitalName || t('app_name')}
            </Typography>}
          </Stack>
          
          <List>
            {menus.map((item, index) => (
              <ListItem 
                key={index} 
                disablePadding 
                sx={{
                  display: 'block',
                  backgroundColor: item.path === location.pathname ? '#FDF2FA' : 'transparent',
                  mb: 1,
                  '& .MuiListItemButton-root': {
                    py: 1.5
                  }
                }}
              >
                <ListItemButton 
                  onClick={()=>{ runNavigation(item.path) }}
                >
                  <ListItemIcon sx={{ 
                    color: item.path === location.pathname ? '#FF43AA' : '#666666',
                    minWidth: '40px'
                  }}>
                    <img 
                      src={item.path === location.pathname ? item.icon.active : item.icon.inactive} 
                      alt={item.displayName}
                      style={{ 
                        width: item.name === 'menu_sensors' ? '18px' : '24px', 
                        height: item.name === 'menu_sensors' ? '19px' : '24px',
                        objectFit: 'contain',
                        filter: item.path === location.pathname ? 'brightness(0) saturate(100%) invert(20%) sepia(100%) saturate(10000%) hue-rotate(320deg) brightness(100%) contrast(100%)' : 'none'
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.displayName}
                    sx={{
                      '& .MuiTypography-root': {
                        color: item.path === location.pathname ? '#FF43AA' : '#211F20',
                        fontSize: '16px',
                        fontWeight: 500
                      }
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>

          <Stack 
            direction="row" 
            alignItems="center" 
            sx={{
              position: 'absolute',
              bottom: 0,
              width: drawerMaxWidth, 
              px: 2, 
              py: 2,
              borderTop: '1px solid #E9ECEF',
              bgcolor: '#FDFBFE',
              // 모바일에서 하단 내비게이션 바에 가려지지 않도록 하단 패딩 추가
              paddingBottom: isMobile ? '64px' : '16px'
            }} 
            onClick={() => { runNavigation('/users/my-info') }}
          >
            <Avatar 
              alt="User Avatar" 
              src={JSON.parse(localStorage.getItem('meInfo'))?.profilePath || noPhotoIcon} 
              onClick={() => {}}
              sx={{width: 32, height: 32, cursor: 'pointer'}}
            />
            <Typography variant="h7" sx={{marginLeft: 2, color: '#333333', fontSize: 16, fontWeight: 500}}>{JSON.parse(localStorage.getItem('meInfo'))?.name}</Typography>
          </Stack>

        </Box>
      </Drawer>
      </>
      :
      <>

      {/* appbar menus */}
      {
      Type === 'AppBar' &&
      <Stack direction="row" alignItems="center" gap={5}>
        <IconButton size="large" edge="start" color="inherit" aria-label="menu">
            <AppIcon/>
        </IconButton>
        {
        menus.map((item, index) => (
        <Typography variant="h7" component="div" sx={{ flexGrow: 1 , "&:hover": { color: "#ccc" } }} onClick={()=>{ runNavigation(item.path) }}>
          {item.displayName}
        </Typography>
        ))
        }
      </Stack>
      }

      {/* drawer menus */}
      {
      Type === 'Drawer' &&
      <>
      <Stack 
        direction="row" 
        alignItems="center" 
        gap={1} 
        sx={{
          marginLeft: !menuOpen ? 8 : 28, 
          minWidth: 480
        }}
      >
        {!menuOpen && (
        <>
          <img src={heartKiumLogo} alt="Heart Kium Logo" style={{ height: '32px', cursor: 'pointer' }} onClick={() => runNavigation('/')}/>
          <Typography 
            variant="subtitle1" 
            component="div"
            sx={{
              color: '#333333',
              fontSize: '16px',
              fontWeight: 500
            }}
            onClick={() => runNavigation('/')}
          >
            {hospitalName || t('app_name')}
          </Typography>
        </>
        )}
        <Typography 
          variant="subtitle1" 
          component="div"
          sx={{
            color: '#333333',
            fontSize: '16px',
            fontWeight: 500
          }}
        >
          {'/ '}
          {breadcrumb.length > 0 && breadcrumb[0] !== '' && breadcrumb.map((item, index) => {
            let menuPath = '/';
            
            if (index === 0) {
              // 첫 번째 항목 (상위메뉴)
              const parentMenu = menus.find(menu => {
                return location.pathname.startsWith(menu.path) && menu.path !== '/';
              });
              if (parentMenu) {
                menuPath = parentMenu.path;
              }
            } else {
              // 두 번째 항목 (하위메뉴)
              const parentMenu = menus.find(menu => {
                return location.pathname.startsWith(menu.path) && menu.path !== '/';
              });
              if (parentMenu && parentMenu.subMenus) {
                const subMenu = parentMenu.subMenus.find(sub => t(sub.name) === item);
                if (subMenu) {
                  menuPath = subMenu.path;
                }
              }
            }
            
            return (
              <span 
                key={index} 
                onClick={() => runNavigation(menuPath)}
                style={{ cursor: 'pointer' }}
              >
                {item}
                {index < breadcrumb.length - 1 && ' / '}
              </span>
            );
          })}
        </Typography>
      </Stack>
      
      <DrawerMenus variant="permanent" open={menuOpen}>

        <DrawerMenusHeader>
          {menuOpen ? (
            <Stack width={drawerMaxWidth} direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" alignItems="center" gap={1}>
                <img src={logoS} alt="HK Logo" style={{ height: '32px', cursor: 'pointer' }} onClick={() => runNavigation('/')} />
                <Typography 
                  variant="subtitle1" 
                  component="div"
                  sx={{
                    maxWidth: 120,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    color: '#333333',
                    fontSize: '16px',
                    fontWeight: 500
                  }}
                  onClick={() => runNavigation('/')}
                  >
                    {hospitalName || t('app_name')}
                </Typography>
              </Stack>
              <IconButton 
                onClick={()=>{setMenuOpen(!menuOpen)}}
                sx={{
                  color: '#666666'
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
            </Stack>
          ) : (
            <Stack sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
              <IconButton 
                onClick={()=>{setMenuOpen(!menuOpen)}}
                sx={{
                  color: '#666666'
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            </Stack>
          )}
        </DrawerMenusHeader>

        <DrawerMenusContent open={menuOpen}>
          <List sx={{ py: 2 }}>
            {menus.map((item, index) => (
              <ListItem 
                key={index} 
                disablePadding 
                sx={{
                  display: 'block',
                  backgroundColor: item.path === location.pathname ? '#FDF2FA' : 'transparent',
                  borderRadius: '8px',
                  mb: 1,
                  '& .MuiListItemButton-root': {
                    py: 1.5,
                    borderRadius: '8px',
                    minHeight: 48,
                    justifyContent: menuOpen ? 'initial' : 'center',
                    px: menuOpen ? 2.5 : 2.2, 
                  }
                }}
              >
                <ListItemButton 
                  onClick={()=>{ runNavigation(item.path) }}                 
                >
                  <ListItemIcon 
                    sx={{ 
                      minWidth: menuOpen ? '40px' : '26px',
                      mr: menuOpen ? 3 : 'auto',
                      justifyContent: 'center',
                    }}
                  >
                    <img 
                      src={item.path === location.pathname ? item.icon.active : item.icon.inactive}
                      alt={item.displayName}
                      style={{ 
                        width: item.name === 'menu_sensors' ? '18px' : '24px', 
                        height: item.name === 'menu_sensors' ? '19px' : '24px',
                        objectFit: 'contain',
                        filter: item.path === location.pathname ? 'brightness(0) saturate(100%) invert(20%) sepia(100%) saturate(10000%) hue-rotate(320deg) brightness(100%) contrast(100%)' : 'none'
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.displayName}
                    sx={{
                      opacity: menuOpen ? 1 : 0,
                      '& .MuiTypography-root': {
                        color: item.path === location.pathname ? '#FF43AA' : '#211F20',
                        fontSize: '16px',
                        fontWeight: 500
                      }
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </DrawerMenusContent>

        <DrawerMenusFooter>
          <Avatar 
            alt="User Avatar" 
            src={JSON.parse(localStorage.getItem('meInfo'))?.profilePath || noPhotoIcon} 
            onClick={() => { runNavigation('/users/my-info') }}
            sx={{width: 32, height: 32, cursor: 'pointer'}}
          />
          {menuOpen &&
            <Typography variant="h7" sx={{marginLeft: 2, color: '#333333', fontSize: 16, fontWeight: 500, maxWidth: 160, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
              {JSON.parse(localStorage.getItem('meInfo'))?.name}
            </Typography>
          }
        </DrawerMenusFooter>

      </DrawerMenus>
      </>
      }

      </>
      }
    </>
  );
};